"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.StringOperandNode = exports.NumberOperandNode = exports.CallOperandNode = exports.PureValueOperandNode = exports.ValueOperandNode = exports.ValueOperandNodeBase = exports.UnaryOperatorNode = exports.BinaryOperatorNode = exports.AstNode = exports.AstNodeType = void 0;
const token_1 = require("../lexer/token");
const model_1 = require("../../model");
const types_1 = require("../../types");
const consts_1 = require("../../formula_parser/consts");
const i18n_1 = require("../../exports/i18n");
var AstNodeType;
(function (AstNodeType) {
    AstNodeType["BinaryOperatorNode"] = "BinaryOperatorNode";
    AstNodeType["UnaryOperatorNode"] = "UnaryOperatorNode";
    AstNodeType["ValueOperandNode"] = "ValueOperandNode";
    AstNodeType["PureValueOperandNode"] = "PureValueOperandNode";
    AstNodeType["CallOperandNode"] = "CallOperandNode";
    AstNodeType["StringOperandNode"] = "StringOperandNode";
    AstNodeType["NumberOperandNode"] = "NumberOperandNode";
})(AstNodeType = exports.AstNodeType || (exports.AstNodeType = {}));
class AstNode {
    constructor(token) {
        this.token = token;
    }
    get numNodes() {
        return 1;
    }
    toString() {
        return `AstNode: ${this.token}::${this.name}`;
    }
}
exports.AstNode = AstNode;
class BinaryOperatorNode extends AstNode {
    constructor(left, token, right) {
        super(token);
        this.name = AstNodeType.BinaryOperatorNode;
        this.left = left;
        this.right = right;
        // TokenType.And, TokenType.Or, TokenType.Add,
        // TokenType.Times, TokenType.Div, TokenType.Minus,
        // TokenType.Mod, TokenType.Concat,
        switch (token.type) {
            // Addition, subtraction, multiplication and division operator symbols are calculated correctly only when both sides are numbers.
            case token_1.TokenType.Add: {
                const isNumberType = ({ valueType, innerValueType, token }) => {
                    return valueType === types_1.BasicValueType.Number || innerValueType === types_1.BasicValueType.Number || token.value.toUpperCase() === 'BLANK';
                };
                if ([left, right].every(isNumberType)) {
                    this.valueType = types_1.BasicValueType.Number;
                    return;
                }
                this.valueType = types_1.BasicValueType.String;
                return;
            }
            case token_1.TokenType.Minus:
            case token_1.TokenType.Times:
            case token_1.TokenType.Mod:
            case token_1.TokenType.Div: {
                this.valueType = types_1.BasicValueType.Number;
                return;
            }
            case token_1.TokenType.Or:
            case token_1.TokenType.And:
            case token_1.TokenType.Equal:
            case token_1.TokenType.NotEqual:
            case token_1.TokenType.Greater:
            case token_1.TokenType.GreaterEqual:
            case token_1.TokenType.Less:
            case token_1.TokenType.LessEqual: {
                this.valueType = types_1.BasicValueType.Boolean;
                return;
            }
            case token_1.TokenType.Concat: {
                this.valueType = types_1.BasicValueType.String;
                return;
            }
            default: {
                throw new TypeError((0, i18n_1.t)(i18n_1.Strings.function_err_unknown_operator, {
                    type: token.type,
                }));
            }
        }
    }
    get numNodes() {
        return 1 + this.left.numNodes + this.right.numNodes;
    }
}
exports.BinaryOperatorNode = BinaryOperatorNode;
class UnaryOperatorNode extends AstNode {
    constructor(child, token) {
        super(token);
        this.name = AstNodeType.UnaryOperatorNode;
        this.child = child;
        switch (token.type) {
            case token_1.TokenType.Minus:
                this.valueType = types_1.BasicValueType.Number;
                break;
            case token_1.TokenType.Not:
                this.valueType = types_1.BasicValueType.Boolean;
                break;
            case token_1.TokenType.Add:
                this.valueType = child.valueType;
                break;
            default:
                throw new Error(`unreachable ${token.value}`);
        }
    }
    get numNodes() {
        return 1 + this.child.numNodes;
    }
}
exports.UnaryOperatorNode = UnaryOperatorNode;
class ValueOperandNodeBase extends AstNode {
    constructor(token) {
        super(token);
        this.value = token.value.replace(/\\(.)/g, '$1');
    }
    init(fieldId, context, hostField) {
        this.context = context;
        fieldId = fieldId.replace(/\\(.)/g, '$1');
        if (fieldId === consts_1.ROLLUP_KEY_WORDS && hostField) {
            this.field = hostField;
            this.valueType = types_1.BasicValueType.Array;
        }
        else {
            const field = context.fieldMap[fieldId];
            if (!field) {
                throw new Error((0, i18n_1.t)(i18n_1.Strings.function_err_invalid_field_name, {
                    fieldId,
                }));
            }
            this.field = field;
            this.valueType = model_1.Field.bindContext(field, context.state).basicValueType;
        }
        const innerValueType = model_1.Field.bindContext(this.field, context.state).innerBasicValueType;
        this.innerValueType = innerValueType || types_1.BasicValueType.String;
    }
}
exports.ValueOperandNodeBase = ValueOperandNodeBase;
class ValueOperandNode extends ValueOperandNodeBase {
    constructor(token, context, hostField) {
        super(token);
        this.name = AstNodeType.ValueOperandNode;
        const fieldId = token.value.slice(1, -1);
        this.init(fieldId, context, hostField);
    }
}
exports.ValueOperandNode = ValueOperandNode;
class PureValueOperandNode extends ValueOperandNodeBase {
    constructor(token, context, hostField) {
        super(token);
        this.name = AstNodeType.PureValueOperandNode;
        const fieldId = token.value;
        this.init(fieldId, context, hostField);
    }
}
exports.PureValueOperandNode = PureValueOperandNode;
class CallOperandNode extends AstNode {
    constructor(token) {
        super(token);
        this.name = AstNodeType.CallOperandNode;
        this.params = [];
        this.value = token.value;
    }
    get numNodes() {
        return this.params.reduce((num, node) => num + node.numNodes, 1);
    }
}
exports.CallOperandNode = CallOperandNode;
class NumberOperandNode extends AstNode {
    constructor(token) {
        super(token);
        this.name = AstNodeType.NumberOperandNode;
        this.valueType = types_1.BasicValueType.Number;
        this.value = token.value;
    }
}
exports.NumberOperandNode = NumberOperandNode;
class StringOperandNode extends AstNode {
    constructor(token) {
        super(token);
        this.name = AstNodeType.StringOperandNode;
        this.valueType = types_1.BasicValueType.String;
        let tokenValue = token.value;
        const terminatorMap = new Map([
            [/\\n/g, '\n'],
            [/\\r/g, '\r'],
            [/\\t/g, '\t'], // tab 
        ]);
        terminatorMap.forEach((v, k) => {
            tokenValue = tokenValue.replace(k, v);
        });
        tokenValue = tokenValue.replace(/\\(.)/g, '$1');
        this.value = tokenValue;
    }
}
exports.StringOperandNode = StringOperandNode;
//# sourceMappingURL=ast.js.map